import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Typography, Container, 
  Paper, FormControl, InputLabel, Select, MenuItem, Dialog, 
  DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { doc, setDoc, getDocs, collection, deleteDoc, updateDoc } from 'firebase/firestore';
import { firestore } from '../firebase.config';
import { useNavigate } from 'react-router-dom';
import PageNavBar from '../components/PageNavBar';
import { useUserContext } from '../context/UserContext';
import IconButton from "@mui/material/IconButton";
import Fab from "@mui/material/Fab";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import TenantBanner from "../components/TenantBanner";



const TenantManagement = () => {
  const [tenantName, setTenantName] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminName, setAdminName] = useState('');
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const navigate = useNavigate();
  const [userList, setUserList] = useState([]);
  const [selectedAdmin, setSelectedAdmin] = useState("");
  const [tenantList, setTenantList] = useState([]);
  const { setSelectedTenantId, setSelectedTenantName } = useUserContext();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTenantId, setEditTenantId] = useState("");
  const [editTenantData, setEditTenantData] = useState({ 
    tenantName: "", 
    tenantId: "",
    adminName: "",
    adminEmail: ""
  });

  useEffect(() => {
    const fetchUsers = async () => {
      const snapshot = await getDocs(collection(firestore, "Users"));
      const users = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUserList(users);
    };
    fetchUsers();
  }, []);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // or "error"
  });

  const handleCreateTenant = async () => {
    if (!tenantName || !tenantId || !adminEmail || !adminName) {
      setError('All fields are required');
      return;
    }
    try {
      // 1. Create TenantMaster entry
      await setDoc(doc(firestore, 'TenantMaster', tenantId), {
        tenantId,
        tenantName,
        adminEmail,
        adminName,
        createdAt: new Date().toISOString(),
      });
      // 2. Create Admin User for the tenant
      await setDoc(doc(firestore, 'Users', adminEmail), {
        userName: adminName,
        userEmail: adminEmail,
        role: 'admin',
        tenantId,
        Is_Active: true,
      });
      setSuccessMsg(`Tenant '${tenantName}' created successfully with admin '${adminEmail}'.`);
      setTenantName('');
      setTenantId('');
      setAdminEmail('');
      setAdminName('');
      setError('');
    } catch (err) {
      console.error('Error creating tenant:', err);
      setError('Failed to create tenant. Please try again.');
    }
  };

  const handleSelectTenant = (tenant) => {
    setSelectedTenantId(tenant.tenantId);
    setSelectedTenantName(tenant.tenantName);
    sessionStorage.setItem("selectedTenantId", tenant.tenantId);
    sessionStorage.setItem("selectedTenantName", tenant.tenantName);
  };

  const fetchTenants = async () => {
  const snapshot = await getDocs(collection(firestore, "TenantMaster"));
  const tenants = snapshot.docs.map((doc) => doc.data());
  setTenantList(tenants);
};


  const handleEditTenant = (tenant) => {
    setEditTenantId(tenant.tenantId);
    setEditTenantData({
      tenantName: tenant.tenantName,
      tenantId: tenant.tenantId,   
      adminName: tenant.adminName || '',
      adminEmail: tenant.adminEmail || ''
    });    
    setEditDialogOpen(true);
  };

  const handleUpdateTenant = async () => {
  try {
    const tenantDocRef = doc(firestore, "TenantMaster", editTenantId);
    await updateDoc(tenantDocRef, {
      tenantName: editTenantData.tenantName,
      adminName: editTenantData.adminName,
      adminEmail: editTenantData.adminEmail
    });
    setSnackbar({
      open: true,
      message: "Tenant updated successfully",
      severity: "success",
    });
    setEditDialogOpen(false);
    fetchTenants(); // Refresh list
  } catch (err) {
    console.error("Error updating tenant:", err);
      setSnackbar({
      open: true,
      message: "Failed to delete tenant",
      severity: "error",
      });
    }
  };


  const handleDeleteTenant = async (id) => {
    const confirm = window.confirm("Are you sure you want to delete this tenant?");
    if (confirm) {
      await deleteDoc(doc(firestore, "TenantMaster", id));
      setSuccessMsg("Tenant deleted");
    }
  };
  useEffect(() => {
    const fetchTenants = async () => {
      const snapshot = await getDocs(collection(firestore, "TenantMaster"));
      const tenants = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTenantList(tenants);
    };
    fetchTenants();
  }, [successMsg]); // Refresh list after creating new tenant


  return (
    
    <Container>
      <PageNavBar title="Tenant Management" backTo="/dashboard" />
      <TenantBanner />
      <Box mt={3} mb={4}>
          <Typography variant="h6" gutterBottom>Tenant List</Typography>
          <Paper sx={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead style={{ backgroundColor: "#f0f0f0" }}>
                <tr>
                  <th style={thStyle}>Tenant ID</th>
                  <th style={thStyle}>Tenant Name</th>
                  <th style={thStyle}>Admin Name</th>
                  <th style={thStyle}>Admin Email ID</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenantList.map((tenant) => (
                  <tr key={tenant.id}>
                    <td style={tdStyle}>{tenant.tenantId}</td>
                    <td style={tdStyle}>{tenant.tenantName}</td>
                    {/* <td style={tdStyle}>{tenant.adminName || "—"}</td>
                    <td style={tdStyle}>{tenant.adminEmail || "—"}</td> */}
                    <td style={tdStyle}>
                      {userList
                        .filter(user => user.tenantId === tenant.tenantId && user.role === "admin")
                        .map(user => user.userName)
                        .map((name, idx, arr) => (
                          <span key={name}>
                            {name}
                            {idx < arr.length - 1 && <br />}
                          </span>
                        )) || "—"}
                    </td>
                    <td style={tdStyle}>
                      {userList
                        .filter(user => user.tenantId === tenant.tenantId && user.role === "admin")
                        .map(user => user.userEmail)
                        .map((email, idx, arr) => (
                          <span key={email}>
                            {email}
                            {idx < arr.length - 1 && <br />}
                          </span>
                        )) || "—"}
                    </td>

                    <td style={tdStyle}>                      
                      <IconButton variant="outlined" size="small" onClick={() => handleEditTenant(tenant)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton variant="outlined" size="small" color="error" onClick={() => handleDeleteTenant(tenant.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Paper>
        </Box>
      
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Tenant</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Tenant Name"
            value={tenantName}
            onChange={(e) => setTenantName(e.target.value)}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Tenant ID"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value.toLowerCase().replace(/\s+/g, '-'))}
            helperText="Unique lowercase ID (e.g., omnevu001)"
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Admin Name</InputLabel>
            <Select
              value={adminName}
              onChange={(e) => {
                const selected = userList.find(u => u.userName === e.target.value);
                setAdminName(e.target.value);
                setAdminEmail(selected?.userEmail || "");
              }}
            >
            {userList.map(user => (
              <MenuItem key={user.id} value={user.userName}>
                {user.userName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Admin Email</InputLabel>
            <Select
              value={adminEmail}
              onChange={(e) => {
                const selected = userList.find(u => u.userEmail === e.target.value);
                setAdminEmail(e.target.value);
                setAdminName(selected?.userName || "");
              }}
            >
            {userList.map(user => (
              <MenuItem key={user.id} value={user.userEmail}>
                {user.userEmail}
              </MenuItem>
            ))}
            </Select>
          </FormControl>
          {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
          {successMsg && <Typography color="primary" sx={{ mt: 2 }}>{successMsg}</Typography>}
        </DialogContent>
            <DialogActions>
              <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateTenant} variant="contained">
                Create Tenant
              </Button>
            </DialogActions>
          </Dialog>

        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
          <DialogTitle>Edit Tenant</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Tenant Name"
              value={editTenantData.tenantName}
              onChange={(e) => 
                setEditTenantData({ ...editTenantData, tenantName: e.target.value })}
              sx={{ mt: 2 }}
            />
            <TextField
              fullWidth
              label="Tenant ID"
              value={editTenantData.tenantId}
              disabled
              sx={{ mt: 2 }}
            />
            <FormControl fullWidth sx={{ mt: 2 }}>
  <InputLabel>Admin Name</InputLabel>
  <Select
    value={editTenantData.adminName}
    onChange={(e) => {
      const selected = userList.find((u) => u.userName === e.target.value);
      setEditTenantData({
        ...editTenantData,
        adminName: e.target.value,
        adminEmail: selected?.userEmail || ""
      });
    }}
  >
    {userList.map((user) => (
      <MenuItem key={user.id} value={user.userName}>
        {user.userName}
      </MenuItem>
    ))}
  </Select>
</FormControl>

<FormControl fullWidth sx={{ mt: 2 }}>
  <InputLabel>Admin Email</InputLabel>
  <Select
    value={editTenantData.adminEmail}
    onChange={(e) => {
      const selected = userList.find((u) => u.userEmail === e.target.value);
      setEditTenantData({
        ...editTenantData,
        adminEmail: e.target.value,
        adminName: selected?.userName || ""
      });
    }}
  >
    {userList.map((user) => (
      <MenuItem key={user.id} value={user.userEmail}>
        {user.userEmail}
      </MenuItem>
    ))}
  </Select>
</FormControl>

          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleUpdateTenant}>
              Update
            </Button>
          </DialogActions>
        </Dialog>

      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: "fixed", bottom: 24, right: 24 }}
        onClick={() => setCreateDialogOpen(true)} // Or however you open the add form
      >
        <AddIcon />
      </Fab>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Container>
    
  );
  
};

const thStyle = {
  padding: "10px",
  border: "1px solid #ccc",
  textAlign: "left",
  fontWeight: "bold",
  backgroundColor: "#f5f5f5"
};

const tdStyle = {
  padding: "10px",
  border: "1px solid #ddd"
};

export default TenantManagement;
